import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { parallel as radashParallel } from 'radash';
import { firstValueFrom } from 'rxjs';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { AddEntityButtonComponent } from '@connect-services/client/components/add-entity-button/add-entity-button.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { GeographicEditButtonComponent } from '@connect-services/client/components/geographic-edit-button/geographic-edit-button.component';
import { MapFieldInterventionSource, MapLayerSource } from '@connect-services/client/components/map/map.component';
import {
    LayerVisibilityButtonState,
    VisibilityButtonComponent,
} from '@connect-services/client/components/visibility-button/visibility-button.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { InformationSchemaTableExtended, LayersService } from '@connect-services/client/sdk/api';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapCreationGeomService } from '@connect-services/client/services/map-creation-geom.service';
import {
    EditModeType,
    LayerEditUiConfig,
    MapEditModeService,
} from '@connect-services/client/services/map-edit-mode.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SourcesActivationService } from '@connect-services/client/services/source-activation.service';
import { SourcesVisibilityService, SourceTypeEnum } from '@connect-services/client/services/sources-visibility.service';
import { UrlSourceService } from '@connect-services/client/services/url-source.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

export type SourceType = MapFieldInterventionSource | MapLayerSource;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AddEntityButtonComponent, GeographicEditButtonComponent, RessourcesModule, VisibilityButtonComponent],
    selector: 'app-source-item',
    standalone: true,
    templateUrl: './source-item.component.html',
})
export class SourceItemComponent implements OnInit {
    public readonly canBeEdited = input<boolean>(true);
    public readonly canToggleVisibility = computed<boolean>(() => {
        if (this.isFiltered() || this.isAnEditLayer()) {
            return false;
        }

        if (this.isActive()) {
            return true;
        }

        return !this.isDataLayerSource();
    });
    public readonly depositName = input<string | null | undefined>(null);
    public readonly disabled = input<boolean>(false);
    public readonly hasTabView = input<boolean>(false);
    public readonly isActive = computed(() => {
        return this.sources().some((source: SourceType) => this.sourcesActivationService.get(source.sourceId));
    });
    public readonly isAddEntityActive = computed(() => {
        return (
            this.sources().some((source) => this.mapEditModeService.activeEditLayerIds().has(source.sourceId)) &&
            this.mapEditModeService.isAddEntityModeActive()
        );
    });
    public readonly isDataLayerSource = input<boolean>(false);
    public readonly isFiltered = input<boolean | undefined>();
    public readonly isGeographicEditActive = computed(
        () =>
            this.sources().some((source) => this.mapEditModeService.activeEditLayerIds().has(source.sourceId)) &&
            this.mapEditModeService.isGeographicEditModeActive(),
    );

    public readonly isSourceTheme = input<boolean | undefined>();
    public readonly isVisible = computed(() =>
        this.sources().some((source) => this.sourcesVisibilityService.isVisible(source)),
    );
    public readonly layerEditConfig = computed<LayerEditUiConfig | null>(() => {
        if (this.layerEditConfigInput()) {
            return this.layerEditConfigInput();
        }

        if (!this.isDataLayerSource()) {
            return null;
        }

        return this.mapEditModeService.getLayerEditConfig({
            showAddEntity: true,
            showGeographicEdit: true,
        });
    });
    public readonly layerEditConfigInput = input<LayerEditUiConfig | null>(null);
    public readonly legendImageUrl: WritableSignal<string | null> = signal(null);
    public readonly openTabView = output<MouseEvent>();
    public readonly sources: InputSignal<SourceType[]> = input.required<SourceType[]>();
    public readonly sourceType: InputSignal<SourceTypeEnum> = input.required<SourceTypeEnum>();
    public readonly visibilityButtonState = computed<LayerVisibilityButtonState>(() => {
        if (!this.sources().length) {
            return LayerVisibilityButtonState.HiddenDisabled;
        }

        if (this.isAnEditLayer()) {
            return LayerVisibilityButtonState.VisibleDisabled;
        }

        const isInZoomRange = this.sources().some((source: SourceType) =>
            this.sourcesVisibilityService.isSourceInZoomRange(source),
        );

        return this.sourcesVisibilityService.mapVisibilityButtonState(
            this.canToggleVisibility(),
            this.isVisible(),
            isInZoomRange,
        );
    });
    public readonly wmsUrl = input<string | null>(null);

    protected readonly allLayerHasSameBookmarkedColumn = signal<boolean>(true);
    protected readonly contentLayoutService = inject(ContentLayoutService);
    protected readonly editableLayerIds = computed<string[]>(() =>
        this.sources()
            .map((source) => source.sourceId)
            .filter((sourceId: string) => this.projectEditModeService.editableLayerIds().includes(sourceId)),
    );
    protected readonly editModeStateService = inject(EditModeStateService);
    protected readonly isAnEditLayer = computed(
        () =>
            !!this.sources().filter((source) => this.mapEditModeService.activeEditLayerIds().has(source.sourceId))
                .length,
    );
    protected readonly isGeomEditionAvailable = computed<boolean>((): boolean => {
        const isLayerWithNoEditableGeom = this.sources().some(
            (source) =>
                !!this.projectEditModeService
                    .geomNoEditableLayerIdsWithReason()
                    .find((geomNoEditableLayerWithReason) => geomNoEditableLayerWithReason.id === source.sourceId),
        );

        return !isLayerWithNoEditableGeom && !this.contentLayoutService.isDataTableView();
    });
    protected readonly isLayerEditable = computed<boolean>(() =>
        this.sources().some((source: SourceType) =>
            this.projectEditModeService.editableLayerIds().includes(source.sourceId),
        ),
    );
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly noGeomEditionReason = computed<string | undefined>(() => {
        const layerIdWithReasonForUnavailability = this.projectEditModeService
            .geomNoEditableLayerIdsWithReason()
            .find((geomNoEditableLayerIdWithReason) =>
                this.sources().some((source) => source.sourceId === geomNoEditableLayerIdWithReason.id),
            );

        return layerIdWithReasonForUnavailability?.reasonForUnavailability;
    });

    private readonly layersService = inject(LayersService);
    private readonly mapCreationGeomService = inject(MapCreationGeomService);
    private readonly mapModalService = inject(MapModalService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly selectionService = inject(SelectionService);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly sourcesActivationService = inject(SourcesActivationService);
    private readonly sourcesVisibilityService = inject(SourcesVisibilityService);
    private readonly translocoService = inject(TranslocoService);
    private readonly urlSourceService = inject(UrlSourceService);
    private readonly waitForMapLoad = signal<boolean>(false);

    public constructor() {
        effect(() => {
            if (!MapBaseService.isMapReady() || !this.waitForMapLoad()) {
                return;
            }

            this.addEntity();
        });
    }

    public async ngOnInit(): Promise<void> {
        await this.setupLayerCreationPossibility();

        if (!this.wmsUrl()) {
            return;
        }

        await this.getWmsLegend(this.wmsUrl()!);
    }

    protected async addEntity(): Promise<void> {
        if (!(await this.discardCurrentSelectionIfAllowed())) {
            return;
        }

        this.selectionService.isSelectionActive.set(false);
        this.mapModalService.close(MapModal.SELECTION);

        if (this.contentLayoutService.isDataTableView()) {
            this.contentLayoutService.toggleIsDataView();
            this.waitForMapLoad.set(true);

            return;
        }
        this.waitForMapLoad.set(false);

        this.toggleEditLayerVisibility();

        const isReClickOnSameLayer = this.mapEditModeService.isReClickOnSameLayer(
            this.editableLayerIds(),
            EditModeType.ADD,
        );
        this.mapEditModeService.toggleLayerEditMode(this.editableLayerIds(), EditModeType.ADD);

        if (!isReClickOnSameLayer) {
            await this.mapCreationGeomService.getAdditionType();

            return;
        }

        this.mapEditModeService.activeGeometryType.set(null);
    }

    protected changeSourceVisibility(event: MouseEvent): void {
        event.stopPropagation();

        this.sourcesVisibilityService.changeSourceVisibility(this.sources(), !this.isVisible());
    }

    protected async editGeometry(): Promise<void> {
        if (!(await this.discardCurrentSelectionIfAllowed())) {
            return;
        }

        this.toggleEditLayerVisibility();

        this.mapEditModeService.toggleLayerEditMode(this.editableLayerIds(), EditModeType.GEOGRAPHIC);
    }

    private areColumnsIdentical(layersInformations: InformationSchemaTableExtended[][]): boolean {
        if (layersInformations.length <= 1) {
            return true;
        }

        const referenceAttributes: string[] =
            layersInformations[0].map((information: InformationSchemaTableExtended) => information.name) || [];
        const referenceLength: number = referenceAttributes.length;

        const referenceSignature: string = [...referenceAttributes].sort().join(',');

        for (let index: number = 1; index < layersInformations.length; index++) {
            const currentColumns: string[] =
                layersInformations[index].map((information: InformationSchemaTableExtended) => information.name) || [];

            if (currentColumns.length !== referenceLength) {
                return false;
            }

            const currentSignature: string = [...currentColumns].sort().join(',');
            if (currentSignature !== referenceSignature) {
                return false;
            }
        }

        return true;
    }

    private canDiscardCurrentSelection(): Promise<boolean> {
        if (!this.selectionStateService.hasSelectedEntities()) {
            return Promise.resolve(true);
        }

        return this.confirmLosingSelection();
    }

    private async confirmLosingSelection(): Promise<boolean> {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('quit'),
                description: this.translocoService.translate('leaveSelectionTool'),
                title: this.translocoService.translate('areYouSureToLeaveSelectionTool'),
                type: ModalType.DANGER,
            },
            width: '36rem',
        });

        const modalResult: ModalResultEnum | undefined = await firstValueFrom(dialogRef.afterClosed());

        return modalResult === ModalResultEnum.CONFIRM;
    }

    private async discardCurrentSelectionIfAllowed(): Promise<boolean> {
        const canDiscardSelection: boolean = await this.canDiscardCurrentSelection();

        if (!canDiscardSelection) {
            return false;
        }

        this.selectionStateService.clearEntitiesAndLayer();

        return true;
    }

    private async getWmsLegend(url: string): Promise<void> {
        const layerName = this.urlSourceService.getLayerName(url);
        const getCapabilities = this.urlSourceService.generateGetCapabilitiesUrl(url);

        try {
            this.legendImageUrl.set(await this.urlSourceService.getLegendUrlFromXml(getCapabilities, layerName));
        } catch {
            this.legendImageUrl.set(null);
        }
    }

    private async setupLayerCreationPossibility(): Promise<void> {
        if (this.sources().length <= 1) {
            return;
        }

        let layersInformations: InformationSchemaTableExtended[][];

        try {
            layersInformations = await radashParallel(
                5,
                this.sources(),
                (source: SourceType): Promise<InformationSchemaTableExtended[]> => {
                    return firstValueFrom(
                        this.layersService.layerControllerGetAttributesInformation({ id: source.sourceId }),
                    );
                },
            );
        } catch {
            this.allLayerHasSameBookmarkedColumn.set(false);

            return;
        }

        this.allLayerHasSameBookmarkedColumn.set(this.areColumnsIdentical(layersInformations));
    }

    private toggleEditLayerVisibility(): void {
        if (this.isVisible()) {
            return;
        }

        this.sourcesVisibilityService.changeSourceVisibility(this.sources(), true);
    }
}
